import { useCallback } from 'react';

/**
 * Hook to play a synthetic notification sound using the Web Audio API
 * This avoids needing external audio files.
 */
export const useNotificationSound = () => {
  const playSound = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      const audioCtx = new AudioContext();

      // Create oscillators and gain nodes
      const oscillator1 = audioCtx.createOscillator();
      const oscillator2 = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      // Connect nodes
      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      // Define sound parameters (sweet "ding" bell sound)
      oscillator1.type = 'sine';
      oscillator2.type = 'sine';

      const now = audioCtx.currentTime;

      // Play a quick two-tone bell (e.g. C6 then E6)
      oscillator1.frequency.setValueAtTime(1046.5, now); // C6
      oscillator2.frequency.setValueAtTime(1318.51, now + 0.1); // E6

      // Envelope: quick attack, slow decay
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.5, now + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

      // Start and stop
      oscillator1.start(now);
      oscillator1.stop(now + 0.5);

      oscillator2.start(now + 0.1);
      oscillator2.stop(now + 0.6);

      // Clean up after it's done
      setTimeout(() => {
        if (audioCtx.state === 'running') {
          audioCtx.close().catch(console.error);
        }
      }, 1000);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }, []);

  return playSound;
};

export default useNotificationSound;
